import { create } from 'zustand';
import { _cardSizeDelta, _cardSizeMultiplier, _cardSizeMultiplierMax, _cardSizeMultiplierMin, _clipboardCards, _homeNodePath, _lang, _parentMode, _previousPath, _storageVersion, _topSentences, _updateAppTimer } from '../constants';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { customStorage, load, store } from '../foo';
import { logMe } from '../../foo/user';
import { useURLStore } from './URLZustand';

interface GlobalState {
    version: number;
    migrate: () => void;
    pwd: string | undefined;
    setPwd: (pwd: string | undefined) => void;
    
    isGlobalStoreInitialized: boolean;
    initGlobalStorage: () => Promise<void>;

    isParentMode: boolean;
    setIsParentMode: (isParentMode: boolean) => void;

    lang: string;
    updateLang: (lang: string) => void;
    hasChosenLang: boolean;

    homeNodePath: string;
    setHomeNodePath: () => void;

    sentence: string[];
    setSentence: (sentence: string[]) => void;
    deleteLastSentencePart: () => void;

    topSentences: [string[], number][]; /* Array of tuples
    containing lists of strings and numbers.
    Each tuple represents a sentence and its frequency.
    */
    setTopSentences: (topSentences: [string[], number][]) => void;

    clipboardCards: string[];
    setClipboardCards: (clipboardCards: string[]) => void;

    clipboardPecs: {[key: string]: any};
    setClipboardPecs: (clipboardPecs: {[key: string]: any}) => void;

    showLoading: boolean;
    setShowLoading: (showLoading: boolean) => void;

    lastUpdateTimestamp: number;



};

const defaultGlobalState: GlobalState = {
    version: 0,
    migrate: () => {},
    pwd: undefined,
    setPwd: () => {},

    isGlobalStoreInitialized: false,
    initGlobalStorage: async () => {},

    isParentMode: _parentMode,
    setIsParentMode: () => {},

    lang: _lang,
    updateLang: () => {},
    hasChosenLang: false,

    homeNodePath: _homeNodePath,
    setHomeNodePath: () => {},

    sentence: [],
    setSentence: () => {},
    deleteLastSentencePart: () => {},

    topSentences: _topSentences,
    setTopSentences: () => {},

    clipboardCards: _clipboardCards,
    setClipboardCards: () => {},

    clipboardPecs: {},
    setClipboardPecs: () => {},


    showLoading: false,
    setShowLoading: () => {},

    lastUpdateTimestamp: 0,



};
 
/**
 * Basic typescript usage doesn't require anything
 * special except for writing create<State>()(...)
 * instead of create(...)
**/
export const useGlobalStore = create<GlobalState>()(
    subscribeWithSelector( 
        persist(
        (set,get) => ({
            ...defaultGlobalState,

            setPwd: (pwd) => set({ pwd: pwd }),
            
            setIsParentMode: (isParentMode) => set({ isParentMode: isParentMode }),

            migrate: async () => {
                if(get().version === 0){
                    const lang = await load("lang", null);
                    const homePage = await load("homePage", null);
                    const hasChosenLang = await load("hasChosenLang", null);
                   
                    set({
                            ...defaultGlobalState,
                            lang: lang !== null ? lang : defaultGlobalState.lang,
                            homeNodePath: homePage !== null ? homePage : defaultGlobalState.homeNodePath,
                            hasChosenLang: hasChosenLang !== null ? hasChosenLang : defaultGlobalState.hasChosenLang,
                            version: _storageVersion
    
                    });
    
                    // delete keys
                    customStorage.removeItem("lang");
                    customStorage.removeItem("homePage");
                    customStorage.removeItem("path2CardsSize");
                    customStorage.removeItem("previousPath");
                    customStorage.removeItem("hasChosenLang");
                    customStorage.removeItem("pwd");

                    window.location.reload(); // this will avoid first time problem with
                    // non-reaction of component because no persisted data exists yet
                }
                
            },
            
            initGlobalStorage: async () => {
                await get().migrate();

                let lastUpdate = get().lastUpdateTimestamp;
                const now = new Date().getTime();
                const diff = now - lastUpdate; 
                if(diff > _updateAppTimer){
                    lastUpdate = now;
                    if ('serviceWorker' in navigator) {
                        navigator.serviceWorker.getRegistration().then(registration => {
                          if (registration) {
                            registration.update();
                          }
                        });
                    }
                }
                set({
                    isGlobalStoreInitialized: true,
                    lastUpdateTimestamp: lastUpdate
                })
            },
            
            updateLang: (lang) => {
                if(lang !== "it"){
                    logMe("updateLang_"+lang, {})
                };
                set({
                    hasChosenLang: true,
                    lang: lang
                })
            },


            setHomeNodePath: () => {
                const { URLVars } = useURLStore.getState();
                set({ homeNodePath: URLVars.nodePath });
            },

            setSentence: (sentence) => set({ sentence: sentence }),
            
            deleteLastSentencePart: () => {
                const sentence = [...useGlobalStore.getState().sentence];
                sentence.pop();
                set({ sentence: sentence });
            },

            setTopSentences: (topSentences) => set({ topSentences: topSentences }),

            setClipboardCards: (clipboardCards) => set({ clipboardCards: clipboardCards }),

            setClipboardPecs: (clipboardPecs) => set({ clipboardPecs: clipboardPecs }),

            setShowLoading: (showLoading) => set({ showLoading: showLoading }),

        
        }),
        {
            name: "globalState",
            storage: customStorage,
            partialize: (state) => ({
                    pwd: state.pwd,
                    lang: state.lang,
                    homeNodePath: state.homeNodePath,
                    hasChosenLang: state.hasChosenLang,
                    version: state.version,
                    lastUpdateTimestamp: state.lastUpdateTimestamp,
                    topSentences: state.topSentences,
                    isParentMode: state.isParentMode
            }),
            onRehydrateStorage: () => async (state, error) => {
                if (!error && state) {
                  await state.initGlobalStorage();
                }
              }, 
        }
        )
    )
);


